//@ts-nocheck
//@ts-ignore
import {
  applySnapshot,
  cast,
  flow,
  getEnv,
  getParent,
  getSnapshot,
  types,
} from "mobx-state-tree";
import { parentRequestsFunction } from "../../home-tutoring/request-flow/request-fns";
import {
  applyFilters,
  calculateTutorAge,
  newGenerateSearchResults,
} from "../../home-tutoring/request-flow/search-fns";
import {
  generatePricingIdeas,
  interpretTutorData,
} from "../../home-tutoring/request-flow/tutor-fns";
import { average, isDefined } from "../../utils/functions";
import {
  IAdminSearchStore,
  IExtendedTutorData,
  ISplitRequestStore,
} from "../types";
import SearchResultType from "./searchResult";

export const getGenderOptions = (searchData) => {
  let gender = searchData.map((tutor) => tutor.gender);
  return Array.from(new Set(gender));
};

function sortAlphabetically(array) {
  return [...array].sort((a, b) => {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  });
}

export const getEducationDegrees = (searchData) => {
  let degrees = [];
  searchData.forEach((tutor) =>
    tutor.education.forEach((edu) => degrees.push(edu.degree))
  );

  return sortAlphabetically(Array.from(new Set(degrees.filter(Boolean))));
};

export const getMaximumAge = (searchData) => {
  let tutorAges = searchData.map((tutor) => calculateTutorAge(tutor));
  return Math.max(...tutorAges);
};

const getMaxMinAvgPrice = (searchData, requestData, searchObj) => {
  let tutorPrices = searchData.map(
    (tutor) => interpretTutorData(tutor, requestData, searchObj).price.amount
  );

  return {
    maximum: Math.max(...tutorPrices).toFixed(),
    minimum: Math.min(...tutorPrices).toFixed(),
    average: average(tutorPrices).toFixed(),
  };
};

const excludedGrades = ["Other grade", "Not applicable"];

const getGraduatingGrades = (searchData) => {
  let grades = [];
  searchData.forEach((tutor) =>
    tutor.education.forEach((edu) => {
      if (!excludedGrades.includes(edu.grade)) grades.push(edu.grade);
    })
  );
  return sortAlphabetically(Array.from(new Set(grades.filter(Boolean))));
};

export const getEducationCountries = (searchData: IExtendedTutorData[]) => {
  let countries = [];
  searchData.forEach((tutor) =>
    tutor.education.forEach((edu) => countries.push(edu.country))
  );
  return sortAlphabetically(Array.from(new Set(countries.filter(Boolean))));
};

export const hasPremiumTutors = (searchData: IExtendedTutorData[]) => {
  let premiumTutors = searchData.filter((tutor) => tutor.level === "premium");
  return premiumTutors.length > 0;
};

export const hasNewTutors = (searchData: IExtendedTutorData[]) => {
  let premiumTutors = searchData.filter((tutor) => tutor.isNewTutor);
  return premiumTutors.length > 0;
};

export const notTaughtTutors = (searchData: IExtendedTutorData[]) => {
  let hasTaught = searchData.filter((tutor) => tutor.lessonsTaught > 0);
  return hasTaught.length > 0;
};

export const getCurriculumOptions = (searchData) => {
  let curriculum = [];
  searchData.forEach((tutor) => curriculum.push(...tutor.curriculum));
  return [...new Set([...curriculum])];
};

const CurrencyType = types
  .model("CurrentType", {
    toCurrency: types.optional(types.string, "₦"),
    fromCurrency: types.maybe(types.string),
    countryFromIP: types.maybe(types.string),
    priceIsFetched: types.optional(types.boolean, false),
  })
  .views((self) => {
    return {
      generateAmount(initialAmount) {
        const exchangeRates = {
          // "₦": { "₵": 50, S: 2, $: 200, "£": 250, "€": 250 },
          // "₵": { "₦": 0.01, S: 45, $: 100, "£": 150, "€": 150 },
          // S: { "₦": 30, "₵": 15, $: 100, "£": 150, "€": 150 },

          "₦": { $: 200, "£": 250, "€": 250 },
          "₵": { "₦": 0.01, $: 100, "£": 150, "€": 150 },
          S: { "₦": 30, $: 100, "£": 150, "€": 150 },
        };

        let isSameCountry = self.toCurrency === self.fromCurrency;

        let exchangeFactor =
          self.fromCurrency && self.toCurrency
            ? isSameCountry
              ? 1
              : exchangeRates[self.fromCurrency][self.toCurrency]
            : 1;

        let newAmount = Math.round(initialAmount / exchangeFactor);
        return newAmount;
      },
    };
  })
  .actions((self) => {
    const { adapter } = getEnv(self);
    const setCurrencyFromCountry = flow(function* setCurrencyFromCountry() {
      let countryFromIP = self.countryFromIP || "Nigeria";
      if (countryFromIP) {
        try {
          self.priceIsFetched = false;
          let { from, to } = yield adapter.resolveCurrencyFromCountry(
            countryFromIP,
            countryFromIP
          );
          if (!self.toCurrency) {
            self.toCurrency = to;
          }
          self.fromCurrency = from;
          self.priceIsFetched = true;
        } catch (error) {
          throw error;
        }
      }
    });
    return {
      // afterCreate() {
      //   setCurrencyFromCountry();
      // },
      updateData(data) {
        applySnapshot(self, { ...getSnapshot(self), ...data });
      },
      setCurrencyFromCountry,
      setSourceCountry(country) {
        self.countryFromIP = country;
      },
    };
  });

export const DiscountCoupon = types
  .model("DiscountCouponStore", {
    discountCode: types.string,
    discountType: types.enumeration("DiscountType", ["percent", "flat"]),
    discount: types.number,
    issuer: types.optional(types.string, ""),
    currency: types.optional(types.string, "N"),
    dateIssued: types.optional(types.string, ""),
    dateExpired: types.string,
    useCount: types.optional(types.number, 0),
    maximumCount: types.maybe(types.number, 0),
  })
  .views((self) => {
    return {
      get hasExpired() {
        let condition2 = new Date(self.dateExpired) > new Date();
        return condition2;
      },
      get isValid() {
        let condition1 = self.maximumCount
          ? self.maximumCount > self.useCount
          : true;
        return condition1 && this.hasExpired;
      },
      totalDiscount(amount) {
        if (self.discountType === "flat") {
          return self.discount;
        }
        return (self.discount * amount) / 100;
      },
    };
  });
export const SearchContextPropType = types
  .model("SearchContextPropType", {
    currentSearchIndex: types.optional(types.number, 0),
    currentTeacherIndex: types.optional(types.number, 0),
    isLoading: types.optional(types.boolean, false),
    _selectedIDS: types.optional(
      types.array(types.maybeNull(types.string)),
      []
    ),
    currentPage: types.optional(types.number, 0),
    resolvedCurrency: types.optional(types.string, "₦"),
    _currentSearchResult: types.optional(types.array(SearchResultType), []),
    currencyStore: types.optional(CurrencyType, {}),
    pageCount: types.optional(types.number, 0),
    discountObj: types.maybe(DiscountCoupon),
    discountErrorMessage: types.optional(types.string, ""),
    hasFetchFailed: types.optional(types.boolean, false),
    hasTutorSwitchFailed: types.optional(types.boolean, false),
    farePerKM: types.optional(types.number, 50),
    distanceThreshold: types.optional(types.number, 5),
    useDiscount: types.optional(types.boolean, true),
    useSearchData: types.optional(types.boolean, false),
  })
  .views((self) => {
    let parent = getParent(self, 1);
    const requestData = parent.requestData;
    const splitRequests = parent.splitRequests;
    // console.log(getSnapshot(parent))
    let {
      contactDetails = {},
      subjectDetails,
      lessonDetails,
      filters = {},
    } = requestData;
    return {
      // get selectedIDS() {
      //   return this.selectedTutorIDs;
      // },
      getTutorByEmail(email: string, key = "email") {
        return self._currentSearchResult.find(
          (o) => o[key].toLowerCase() === email.toLowerCase()
        );
      },
      get isMonthly() {
        return false;
      },
      get isPreloaded() {
        return self.currentSearchResult.length > 0;
      },
      get selectedIDS() {
        let index = this.workingCurrentIndex;
        return this.selectedTutorIDs.map((o, i) => {
          return i === index ? null : o;
        });
        // return self._selectedIDS;
      },
      get speciality() {
        let pp = getParent(self, 1);
        return pp.getSpeciality(this.searchObj.searchSubject);
      },
      getPricingIdea(budget) {
        const searchObj = self.searchObj;
        let pricingIdea = generatePricingIdeas(
          budget,
          self.currentSearchResult,
          requestData,
          searchObj
        );
        return pricingIdea;
      },
      canGetPriceIdeas() {
        let pp = getParent(self, 1);
        const splitRequests = pp.splitRequests;
        const searchObj = self.searchObj;
        let lessonHours = lessonDetails.lessonSchedule.lessonHours;
        let lessonDays = searchObj?.lessonDays?.length || 0;

        if (lessonHours === 1 && lessonDays === 1) return false;
        if (splitRequests.length > 1) return false;
        if (filters.sortBy === "Lowest price first") return true;
      },
      get slug() {
        let pp = getParent(self, 1);
        return pp.slug;
      },
      get searchObj() {
        let pp = getParent(self, 1);
        let splitRequests = this.adminSelectedSplits;
        return splitRequests.length > 0
          ? splitRequests[this.workingCurrentIndex]
          : undefined;
        return pp.splitRequests.length > 0
          ? pp.splitRequests[self.currentSearchIndex]
          : undefined;
        // return ;
      },
      get selectedSplits() {
        return splitRequests.filter((split) => Boolean(split.selectedTutor));
      },
      get adminSelectedSplits() {
        let pp = getParent(self, 1);
        return pp.workingSplitRequest;
      },
      get workingCurrentIndex() {
        let pp = getParent(self, 1);
        let splitRequests = pp.splitRequests;
        return splitRequests.length > 1
          ? self.currentSearchIndex
          : self.currentTeacherIndex;
      },
      get teachersRequested() {
        return splitRequests.length;
      },
      get selectedTeachers() {
        let pp = getParent(self, 1);
        let arr = pp.isAdminLogin
          ? this.adminSelectedSplits
          : this.selectedSplits;
        return arr.map((split) => split?.selectedTutor).filter(Boolean);
      },
      get IfTeacherSelectedIDs() {
        return splitRequests.map((split) => split.selectedTutor?.userId);
      },
      get selectedTutorIDs() {
        return this.selectedTeachers.map((teacher) => teacher.userId);
      },
      get hasSelectedAllTutors() {
        let pp = getParent(self, 1);
        let condition1 = pp.isAdminLogin
          ? this.selectedTeachers.length > 0
          : this.selectedSplits.length === this.teachersRequested;
        return pp.singleCondition
          ? condition1
          : condition1 &&
              pp.useRequestDataProps.selectedTutorsIds.every(Boolean);
      },
      get isParent() {
        return true;
        return contactDetails.customerType === "parent";
      },
      get subject() {
        return isDefined(this.searchObj) &&
          isDefined(this.searchObj.searchSubject)
          ? this.searchObj.searchSubject
          : "";
      },
      get isOneTutor() {
        return splitRequests.length === 1;
      },
      get hasSelectedTutor() {
        return this.selectedSplits.length > 0;
      },
      get selectedTutor() {
        return this.selectedTutorIDs[self.currentSearchIndex]
          ? this.selectedTutorIDs[self.currentSearchIndex]
          : "";
      },
      get genderOptions() {
        return !self.isLoading
          ? getGenderOptions(self.currentSearchResult)
          : [];
      },
      get curriculumOptions() {
        return !self.isLoading
          ? getCurriculumOptions(this.searchResults.flat())
          : [];
      },
      get deliveryOptions() {
        return !self.isLoading
          ? getDeliveryOptions(this.searchResults.flat())
          : [];
      },
      get degreeOptions() {
        return !self.isLoading
          ? getEducationDegrees(this.searchResults.flat())
          : [];
      },
      get gradesOptions() {
        return !self.isLoading
          ? getGraduatingGrades(this.searchResults.flat())
          : [];
      },
      get educationCountries() {
        return !self.isLoading
          ? getEducationCountries(self.currentSearchResult)
          : [];
      },
      get notTaught() {
        return !self.isLoading
          ? notTaughtTutors(this.searchResults.flat())
          : [];
      },
      get hasNew() {
        return !self.isLoading ? hasNewTutors(this.searchResults.flat()) : [];
      },
      get hasPremium() {
        return !self.isLoading
          ? hasPremiumTutors(this.searchResults.flat())
          : [];
      },
      get maximumTutorAge() {
        return !self.isLoading ? getMaximumAge(this.searchResults.flat()) : [];
      },
      get tutorPrices() {
        let pp = getParent(self, 1);

        if (pp.splitRequests.length == 0) {
          return [];
        }
        return !self.isLoading
          ? getMaxMinAvgPrice(
              this.getSearchResults().flat(),
              // this.searchResults.flat(),
              // getSnapshot(self.currentSearchResult),
              pp.requestData,
              getSnapshot(this.searchObj)
            )
          : [];
      },
      get resultsPerPage() {
        let pp = getParent(self, 1);
        return pp.resultsPerPage;
      },
      determineTotalTuition(tutorId, hourlyRate = 0) {
        let pp = getParent(self, 1);
        let tutorInstance = self.currentSearchResult.find(
          (o) => o.userId == tutorId
        );
        if (tutorInstance) {
          let oxt = this.searchObj ? getSnapshot(this.searchObj) : {};
          let searchObj = Object.keys(oxt).length > 0 ? this.searchObj : {};
          return tutorInstance.rawCalculateTuitionFee(
            hourlyRate,
            pp.requestData,
            searchObj
          );
        }
        return 0;
      },
      get searchResults() {
        let pp = getParent(self, 1);
        let xfilters = getSnapshot(pp.filterStore);
        // let tutorOptions = getSnapshot(self.currentSearchResult);
        let requestData = pp.requestData;
        let oxt = this.searchObj ? getSnapshot(this.searchObj) : {};
        let searchObj = Object.keys(oxt).length > 0 ? this.searchObj : {};
        if (pp.splitRequests.length == 0 && pp.displayAllTutors === false) {
          return [];
        }
        // console.log("Current Search!! ", getSnapshot(self.currentSearchResult));
        let uniqueResults = self._currentSearchResult.map((o) => ({
          ...getSnapshot(o),
          activeSubjects: o.activeSubjects,
          newTutorDiscount: self.useDiscount ? o.newTutorDiscount : 0,
          additionalInfo: pp.displaySelectedTutors
            ? o.oldFlowSearchTutorData()
            : o.searchTutorData(requestData, searchObj),
        }));
        let tutorDataOptions = self._currentSearchResult.map((o) =>
          pp.displaySelectedTutors
            ? o.oldFlowSearchTutorData()
            : o.searchTutorData(requestData, searchObj)
        );
        let result = newGenerateSearchResults(
          uniqueResults,
          tutorDataOptions,
          this.resultsPerPage,
          pp.requestData,
          this.searchObj ? getSnapshot(this.searchObj) : undefined,
          xfilters,
          pp.displaySelectedTutors
        );
        // remove selectedTutors and denied tutors
        let excludeTutors = pp.displaySelectedTutors
          ? []
          : [
              // ...this.selectedTutorIDs,
              ...this.selectedIDS,
              ...getSnapshot(pp.deniedTutors),
            ];
        // console.log("excludeTutors", excludeTutors);
        if (excludeTutors.length > 0) {
          return result.map((set) =>
            set.filter((tutor) => !excludeTutors.includes(tutor.userId))
          );
          // return result.filter(
          //   (tutor) => !excludeTutors.includes(tutor.userId)
          // );
        }
        return result;
      },
      getSearchResults() {
        let result = self.searchResults.map((o) => o);
        return self.searchResults;
      },
      hasSearchResults(searchArray: any[]) {
        if (searchArray.length > 0) {
          let firstSearchResult = searchArray[0];
          if (firstSearchResult.length > 0) {
            return true;
          } else return false;
        } else return false;
      },
      getFilteredResults({ sortBy, ..._filters }) {
        const xfilters = { ...getSnapshot(parent.filterStore), ..._filters };
        // let initialResults = getRelevanceScore(
        //   self.searchResults.flat(),
        //   requestData,
        //   self.searchObj
        // );
        // console.log(self.searchResults)
        // return self.searchResults.flat().length;
        const initialResults = self.searchResults.flat();
        let filteredResults = applyFilters(initialResults, xfilters);
        return filteredResults.length;
      },
      generatePaymentInfo() {
        let p = getParent(self, 1);
        let { lessonSchedule, lessonType } = p.requestData.lessonDetails;

        let lessonHours = lessonSchedule ? lessonSchedule.lessonHours : "";

        let daysRequested = lessonSchedule ? lessonSchedule.lessonDays : [];
        let daysSelected = [];
        let selectedSplits: ISplitRequestStore[] = p.isAdminLogin
          ? self.adminSelectedSplits.filter(Boolean)
          : self.selectedSplits;
        selectedSplits
          .filter((split) => split.names.length > 0)
          .map((split) => daysSelected.push(...split.lessonDays));
        daysSelected = [...new Set([...daysSelected])];

        let delivery = Boolean(filters.delivery)
          ? filters.delivery
          : lessonType;

        let lessonPayments = selectedSplits.map((o, i) =>
          p.displaySelectedTutors
            ? o.oldFlowLessonPayments(requestData)
            : o.lessonPayments(
                p.requestData,
                self.distanceThreshold,
                self.farePerKM,
                p.isAdminLogin,
                i
              )
        );
        let tuitionFee = 0,
          totalLessons = 0,
          totalDiscount = 0,
          transportFare = 0;
        if (p.splitRequests.length > 1) {
          for (let i = 0; i < lessonPayments.length; i++) {
            tuitionFee += self.generateAmount(lessonPayments[i].lessonFee);
            totalLessons += lessonPayments[i].lessons;
            if (!p.isAdminLogin) {
              totalDiscount += self.generateAmount(
                lessonPayments[i].firstBookingDiscount
              );
              transportFare += self.generateAmount(
                lessonPayments[i].transportFare
              );
            }
          }
        } else if (p.splitRequests.length == 1 && lessonPayments.length > 0) {
          for (let i = 0; i < 1; i++) {
            tuitionFee += self.generateAmount(lessonPayments[i].lessonFee);
            totalLessons += lessonPayments[i].lessons;
            if (!p.isAdminLogin) {
              totalDiscount += self.generateAmount(
                lessonPayments[i].firstBookingDiscount
              );
              transportFare += self.generateAmount(
                lessonPayments[i].transportFare
              );
            }
          }
        }
        // conditionally apply discount logic only when there isn't any discount applied by tutor.
        let couponDiscount = 0;
        if (self.discountObj) {
          couponDiscount = self.discountObj.totalDiscount(tuitionFee);
          totalDiscount += couponDiscount;
        }
        let appliedDiscount = totalDiscount > 0;
        if (!self.useDiscount) {
          totalDiscount = 0;
          couponDiscount = 0;
        }
        // totalDiscount+ =
        //   totalDiscount > 0
        //     ? totalDiscount
        //     : self.discountObj
        //     ? self.discountObj.totalDiscount(tuitionFee)
        //     : totalDiscount;

        let totalTuition = tuitionFee + transportFare - totalDiscount;
        let isBillableDistance = transportFare > 0 && delivery === "physical";

        let activeTeacherIDs = selectedSplits
          .filter((split) => split.names.length > 0)
          .map((teacher) => teacher?.selectedTutor?.userId);

        const getLessonsPerDay = (day) => {
          let days = selectedSplits.filter(
            (split) => split.lessonDays.includes(day) && split.names.length > 0
          );
          return days.length;
        };

        return {
          lessonPayments,
          totalTuition,
          tuitionFee,
          totalLessons,
          totalDiscount,
          transportFare,
          isBillableDistance,
          activeTeacherIDs,
          daysRequested,
          daysSelected,
          getLessonsPerDay,
          appliedDiscount,
          couponDiscount,
          discountCode: self.discountObj?.discountCode || "",
          hoursOfLesson: lessonHours,
        };
      },
      get toCurrency() {
        return self.currencyStore.toCurrency;
      },
      get priceIsFetched() {
        return self.currencyStore.priceIsFetched;
      },
      generateAmount(amount) {
        return self.currencyStore.generateAmount(amount);
      },
      get currentSearchResult() {
        return self._currentSearchResult;
      },
    };
  })
  .actions((self) => {
    let { adapter } = getEnv(self);
    let parent = getParent(self, 1);
    // const requestData = parent.requestData;
    // let {
    //   splitRequests = [],
    //   contactDetails = {},
    //   subjectDetails,
    //   lessonDetails,
    //   filters = {},
    // } = requestData;
    const updateDiscountCoupon = (result) => {
      self.discountObj = DiscountCoupon.create(result);
    };
    const initializeCurrencyForCountry = flow(
      function* initializeCurrencyForCountry(sourceCountry) {
        if (!self.resolvedCurrency) {
          try {
            let currency = yield adapter.getCurrencyForCountry(sourceCountry);
            self.resolvedCurrency = currency;
          } catch (error) {
            throw error;
          }
        }
      }
    );
    function getAllTestimonials() {
      let promises = self._currentSearchResult.map((o) =>
        o.fetchTestimonials()
      );
      return Promise.all(promises);
    }

    const initializeSearch = flow(function* initializeSearch(
      firstSearch,
      tutorPoolOnly = false
    ) {
      try {
        let result = [];
        if (parent.hasSearch) {
          if (
            firstSearch &&
            Array.isArray(firstSearch) &&
            firstSearch.length > 0
          ) {
            result = firstSearch;
          } else {
            self.isLoading = true;
            let searchData = undefined;
            if (parent.isAdminLogin && !tutorPoolOnly && self.useSearchData) {
              searchData = getSnapshot(parent.requestParameters);
            }
            result = yield adapter.fetchSearchResultFunc(
              self.currentSearchIndex,
              parent.requestData,
              self.specialities,
              tutorPoolOnly,
              searchData
            );
          }
        } else {
          result = firstSearch || [];
        }
        self.isLoading = false;
        result = result.map((o) => {
          let index = parent.previousTutors.find((j) => j.tutorId === o.userId);
          if (index) {
            o.subject.hourlyRate = index.rate;
          }
          return o;
        });
        self._currentSearchResult = cast(result);
        self.currencyStore?.setSourceCountry(result[0]?.country);
        if (parent.hasSearch) {
          if (!parent.isAdminLogin) {
            getAllTestimonials();
          }
        }
      } catch (error) {
        console.log(error);
        self.isLoading = false;
        throw error;
      }
    });
    const onCreate = flow(function* onCreate(couponCode, firstSearch) {
      try {
        yield Promise.all([
          initializeSearch(firstSearch),
          onApplyDiscount(couponCode),
        ]);
        self.currencyStore.updateData({
          toCurrency: self.resolvedCurrency,
          countryFromIP: self.currentSearchResult[0]?.country,
        });
        yield self.currencyStore.setCurrencyFromCountry();
        self.pageCount = self.searchResults.length;
      } catch (error) {
        throw error;
      }
    });
    const saveSelectedTutor = (tutorId, skip = false) => {
      //Todo update reference to parent
      let tutor_id = tutorId || self.selectedTutorIDs[self.workingCurrentIndex];
      let tutorSelected = self.currentSearchResult.find(
        (tutor) => tutor.userId === tutor_id
      );
      parent.updateSelectedTutors(
        getSnapshot(tutorSelected),
        self.workingCurrentIndex,
        skip
      );
    };
    const removeSelectedTutor = () => {
      parent.removeSelectedTutor(self.workingCurrentIndex);
    };
    function getNextSearchIndex() {
      let selectedSplitIndex = parent.splitRequests.map((split, i) => ({
        index: i,
        value: Boolean(split.selectedTutor),
      }));
      let remaining = selectedSplitIndex.slice(self.currentSearchIndex);
      let toNextPageIndex = remaining.filter(
        (o) => o.index !== self.currentSearchIndex
      );
      let foundIndex = null;
      toNextPageIndex.forEach((j) => {
        if (!foundIndex) {
          if (j.value === false) {
            foundIndex = j.index;
          }
        }
      });
      return foundIndex;
    }
    const onNextClick = flow(function* onNextClick() {
      if (!self.hasSelectedAllTutors) {
        try {
          self.currentPage = 0;
          self.isLoading = true;
          let value = getNextSearchIndex();
          if (value) {
            self.currentSearchIndex = value;
          }
          // self.currentSearchIndex += 1;
          self._selectedIDS = self.selectedTutorIDs;
          self._currentSearchResult = cast([]);
          parent.filterStore.resetFilters();
          yield initializeSearch();
          // setSelectedIDs(selectedTutorIDs);
          self.hasFetchFailed = false;
        } catch (error) {
          self.currentSearchIndex -= 1;
          self.hasFetchFailed = true;
        }
      }
    });
    const onApplyDiscount = flow(function* onApplyDiscount(couponText) {
      if (couponText) {
        try {
          self.discountErrorMessage = "";
          let result = yield adapter.verifyCoupon(couponText, self.slug);

          if (!result) {
            throw "Coupon is invalid";
          }

          if (result.hasUsed) {
            throw `${result.discountCode} already used by you`;
          }
          let sample = DiscountCoupon.create(result);
          if (sample.isValid) {
            self.discountObj = DiscountCoupon.create(result);
          } else {
            let error = sample.hasExpired
              ? "This coupon has expired"
              : "This coupon is no longer valid";
            throw error;
          }
        } catch (error) {
          self.discountErrorMessage = error;

          if (!error.includes("already used")) {
            throw error;
          }
        }
      }
    });
    const initializeSearchIndex = flow(function* initalizeSearchIndex() {
      let currentIndex = self.currentSearchIndex;
      let selectedSplitIndex = parent.splitRequests
        .map((split, i) => ({
          index: i,
          value: Boolean(split.selectedTutor),
        }))
        .filter((o) => o.value === false);
      if (selectedSplitIndex.length > 0) {
        self.currentSearchIndex = selectedSplitIndex[0].index;
      }
      if (currentIndex != self.currentSearchIndex) {
        yield initializeSearch();
      }
    });
    const updateCurrentSearchIndex = (index: number) => {
      if (parent.splitRequests.length > 1) {
        self.currentSearchIndex = index;
      } else {
        self.currentTeacherIndex = index;
      }
    };
    const changeCurrentSearchIndex = flow(function* changeCurrentSearchIndex(
      selectedTutor,
      tutorIndex,
      verify = false
    ) {
      // let ids = getSnapshot(self.selectedIDS);
      let ids = self.selectedTutorIDs;
      let index = selectedTutor
        ? ids.findIndex((o) => o === selectedTutor.userId)
        : tutorIndex;
      if ([...new Set(ids)].length === 1 && parent.isAdminLogin) {
        index = tutorIndex;
      }
      if (self.hasTutorSwitchFailed && index < 0) {
        index = self.workingCurrentIndex;
      }
      try {
        // if (index > -1 && index !== self.currentSearchIndex) {
        let condition = index > -1;
        if (verify) {
          condition = condition && index !== self.workingCurrentIndex;
        }
        if (condition) {
          let promises = [initializeSearch()];
          updateCurrentSearchIndex(index);
          if (parent.isAdminLogin) {
            promises.push(parent.updateRequestParametersIndex(index));
            self.useSearchData = false;
          }
          // self.currentSearchIndex = index;
          yield Promise.all(promises);
          self.hasFetchFailed = false;
          self.hasTutorSwitchFailed = false;
          self.currentPage = 0;
        }
      } catch (error) {
        console.log(error);
        self.hasFetchFailed = true;
        self.hasTutorSwitchFailed = true;
      }
    });

    const onApplyFilters = (_filters) => {
      // let lessonType = filters.lessonType;
      // let newLessonDetails = { ...lessonDetails, lessonType };
      // let newRequestData = {
      //   ...requestData,
      //   lessonDetails: newLessonDetails,
      // };
      if (typeof _filters !== "number") {
        parent.onUpdateFilters(_filters);
        self.currentPage = 0;
      }
    };

    const getRawSearchResultSet = flow(
      function* getRawSearchResultSet(filters) {
        onApplyFilters(filters);
        try {
          yield initializeSearch();
        } catch (error) {
          throw error;
        }
      }
    );

    const updateSearchFlexibility = flow(
      function* updateSearchFlexibility(filters) {
        try {
          yield getRawSearchResultSet(filters);
        } catch (error) {
          throw error;
        }
      }
    );
    return {
      // afterCreate() {
      //   onCreate();
      // },
      updateCurrency(currency) {
        self.resolvedCurrency = currency;
      },
      toggleTutorsDiscount(value) {
        self.useDiscount = value;
      },
      changeTutorHourlyRate(tutorId, newRate) {
        console.log({ tutorId, newRate });
      },
      updateDiscountCoupon,
      onCreate,
      setFareData(farePerKM, distancePerThreshold) {
        self.farePerKM = farePerKM;
        self.distanceThreshold = distancePerThreshold;
      },
      initializeSearch,
      initializeCurrencyForCountry,
      onApplyDiscount,
      updateSearchFlexibility,
      initializeSearchIndex,
      onSelectTutor(tutorId) {
        if (self.selectedTutorIDs.includes(tutorId)) {
          if (!parent.isAdminLogin) {
            removeSelectedTutor();
          }
        } else {
          let selected_id =
            tutorId || self.selectedTutorIDs[self.workingCurrentIndex];
          saveSelectedTutor(selected_id);
        }
        // self.selectedIDS = self.selectedTutorIDs
      },
      onApplyFilters,
      getRawSearchResultSet,
      onRemoveFilters() {
        this.onApplyFilters({});
      },
      onNextClick,
      setPageCount(pageCount) {
        self.pageCount = pageCount;
      },
      changeCurrentSearchIndex,
      getSortOptions() {
        let p = getParent(self, 1);
        let sortOptions = [
          "Recommended",
          "Lowest price first",
          "Highest price first",
        ];
        let results = self.currentSearchResult;

        if (results.filter((item) => item.rating > 0).length > 0) {
          sortOptions = [
            ...sortOptions,
            "Number of reviews",
            "Highest ratings",
          ];
        }
        if (results.filter((item) => item.distance > 0).length > 0) {
          sortOptions = [...sortOptions, "Closest distance"];
        }
        if (p.isAdminLogin) {
          sortOptions = [...sortOptions, "New Tutors"];
        }
        return sortOptions;
      },
      setCurrentPage(page) {
        self.currentPage = page;
      },
      getTutorTestimonials(tutor) {
        let instance = self.currentSearchResult.find(
          (o) => o.userId === tutor.userId
        );
        if (instance) {
          return instance.fetchTestimonials();
        }
        return Promise.resolve({});
      },
      updateTutorSubjectInSearchResult(userId, data) {
        const tutorIndex = self._currentSearchResult.findIndex(
          (tutor) => tutor.userId === userId
        );
        // self._currentSearchResult[tutorIndex].subject = data;
        self._currentSearchResult[tutorIndex].updateSubject(data);
        // do a check for the list of selected tutors and update the subject as well
        let selected_id =
          userId || self.selectedTutorIDs[self.workingCurrentIndex];
        saveSelectedTutor(selected_id, true);
      },
    };
  });

export default SearchContextPropType;
